/**
 * Production Logger - Centralized logging with levels
 * Automatically disabled in production to avoid console pollution
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
  }

  private log(level: LogLevel, message: string, data?: any, context?: string) {
    // Only output to console in development
    if (this.isDevelopment) {
      const prefix = context ? `[${context}]` : '';
      const timestamp = new Date().toISOString().split('T')[1].split('.')[0];

      switch (level) {
        case 'debug':
          console.log(`🔍 ${timestamp} ${prefix}`, message, data || '');
          break;
        case 'info':
          console.info(`ℹ️  ${timestamp} ${prefix}`, message, data || '');
          break;
        case 'warn':
          console.warn(`⚠️  ${timestamp} ${prefix}`, message, data || '');
          break;
        case 'error': {
          // Check if it's a network error (backend unavailable)
          const isNetworkError =
            data instanceof TypeError ||
            (data instanceof Error &&
              (data.message.includes('Failed to fetch') ||
                data.message.includes('NetworkError') ||
                data.message.includes('ERR_CONNECTION_REFUSED'))) ||
            (typeof message === 'string' &&
              (message.includes('Failed to fetch') ||
                message.includes('NetworkError') ||
                message.includes('ERR_CONNECTION_REFUSED')));

          // Only log network errors at debug level to avoid console spam
          if (isNetworkError) {
            console.debug(`🔍 ${timestamp} ${prefix}`, message, data || '');
          } else {
            console.error(`❌ ${timestamp} ${prefix}`, message, data || '');
            if (data instanceof Error) {
              console.error(data.stack);
            }
          }
          break;
        }
      }
    }
  }

  /**
   * Debug-level logging (verbose)
   */
  debug(message: string, data?: any, context?: string) {
    this.log('debug', message, data, context);
  }

  /**
   * Info-level logging (normal operations)
   */
  info(message: string, data?: any, context?: string) {
    this.log('info', message, data, context);
  }

  /**
   * Warning-level logging (potential issues)
   */
  warn(message: string, data?: any, context?: string) {
    this.log('warn', message, data, context);
  }

  /**
   * Alias for warn() for backward compatibility
   */
  warning(message: string, data?: any, context?: string) {
    this.warn(message, data, context);
  }

  /**
   * Error-level logging (serious problems)
   */
  error(message: string, error?: any, context?: string) {
    this.log('error', message, error, context);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export as default
export default logger;
